/**
 * /client-engagement/agenda — "AgendaGPT: Meeting Agenda Creator". Native
 * React form posting `multipart/form-data` to the n8n-hosted Form Trigger
 * for the AgendaGPT workflow. Replaces PR #153's external-link shortcut.
 *
 * The submission itself — the URL, the multipart body, the positional
 * `field-0`/`field-1` names and the error-copy split — lives in
 * `features/agenda/submitAgendaForm.ts` since 2026-08-13, because the Search
 * Detail page's AgendaGPT button fires the same request. Read that module's
 * header before changing anything about the request: every property in it is
 * load-bearing against a workflow that answers 200 while silently dropping a
 * submission.
 *
 * Content-only page: the authenticated shell (header/sidebar/footer) is
 * provided by `WebShellLayout` via `<Outlet>`, so this component returns
 * ONLY its page content — same pattern as `EmailTemplateEditor` and
 * `SlideCreator`.
 *
 * This page left the sidebar on 2026-08-13 (`agenda-gpt` flag off) — the
 * capability now lives as a button on Search Detail, where the search id is
 * already known and does not have to be typed. The route stays registered and
 * the form stays working; only its menu entry is hidden.
 */
import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { useMe } from "@nu-assist/shared-fe";
import { Button, PageHeading } from "@nu-assist/ui";

import {
  type AgendaSubmissionError,
  EMAIL_FIELD,
  SEARCH_ID_FIELD,
  agendaErrorMessage,
  submitAgendaForm,
} from "../../features/agenda/submitAgendaForm";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// Copy replicated verbatim from the live n8n form. Styling is the
// nu-assist design system (no NU Advisory branding).
const PAGE_TITLE = "AgendaGPT: Meeting Agenda Creator";
const PAGE_SUBTITLE = "Fill this out to get your meeting agenda emailed to you";
const SEARCH_ID_LABEL = "Search ID";
const SEARCH_ID_PLACEHOLDER =
  "Add the Search ID from the Search URL in Thrive, e.g. 12345";
const EMAIL_LABEL = "Your Email";
const EMAIL_PLACEHOLDER = "sirchops@nusearch.com";
const SUBMIT_LABEL = "Save Me Time!";
const SUBMIT_LABEL_PENDING = "Saving…";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AgendaForm() {
  const me = useMe();

  const [searchId, setSearchId] = useState("");
  const [email, setEmail] = useState("");

  // Prefill the email from the federation profile on mount + whenever the
  // useMe query resolves. Doesn't clobber a user edit — the effect only
  // fires when `email` is still the empty initial state.
  useEffect(() => {
    if (me.status === "authenticated" && email.length === 0) {
      setEmail(me.data.email);
    }
  }, [me.status, me.data?.email, email.length]);

  const submitMutation = useMutation({
    mutationFn: submitAgendaForm,
    onSuccess: () => {
      toast.success("Agenda request submitted");
      // Reset Search ID for the next submission; keep email (so a
      // recruiter submitting several agendas doesn't re-type it).
      setSearchId("");
    },
    onError: (err: AgendaSubmissionError) => {
      // 5xx / network failure ("can't reach the server") versus 4xx ("server
      // understood, rejected — show the why"). The split lives with the
      // request so this page and the Search Detail button cannot disagree.
      toast.error(agendaErrorMessage(err));
    },
  });

  // Defensive narrow — WebShellLayout's AuthGuard already wraps this.
  if (me.status !== "authenticated") return null;

  const isPending = submitMutation.isPending;

  return (
    <div data-testid="agenda-form" className="p-12">
      <div className="max-w-xl">
        <PageHeading
          title={PAGE_TITLE}
          description={PAGE_SUBTITLE}
          className="mb-8"
        />

        <form
          onSubmit={(e) => {
            e.preventDefault();
            submitMutation.mutate({ searchId, email });
          }}
          className="flex flex-col gap-4"
          noValidate={false}
        >
          {/* Search ID */}
          <div className="flex flex-col gap-1">
            <label
              htmlFor="agenda-search-id"
              className="text-label font-medium text-text"
            >
              {SEARCH_ID_LABEL}{" "}
              <span className="text-error" aria-hidden="true">
                *
              </span>
            </label>
            <input
              id="agenda-search-id"
              name={SEARCH_ID_FIELD}
              type="text"
              required
              value={searchId}
              onChange={(e) => setSearchId(e.target.value)}
              placeholder={SEARCH_ID_PLACEHOLDER}
              disabled={isPending}
              className="rounded-sm border border-border bg-surface px-3 py-2 text-body text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50"
            />
          </div>

          {/* Your Email */}
          <div className="flex flex-col gap-1">
            <label
              htmlFor="agenda-email"
              className="text-label font-medium text-text"
            >
              {EMAIL_LABEL}{" "}
              <span className="text-error" aria-hidden="true">
                *
              </span>
            </label>
            <input
              id="agenda-email"
              name={EMAIL_FIELD}
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={EMAIL_PLACEHOLDER}
              disabled={isPending}
              className="rounded-sm border border-border bg-surface px-3 py-2 text-body text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50"
            />
          </div>

          {/* Submit */}
          <div className="mt-2 flex">
            <Button type="submit" disabled={isPending}>
              {isPending ? SUBMIT_LABEL_PENDING : SUBMIT_LABEL}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
